import React from 'react';

import classNames from 'classnames';
import CloseIcon from 'icons/ic-close.svg';
import PropTypes from 'prop-types';
import voca from 'voca';

import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

export default function TextField({
  autoFocus,
  className,
  clearable,
  customMargin,
  fieldError,
  inputClassName,
  inputRef,
  isDisabled,
  isRequired,
  label,
  maxLength,
  min,
  name,
  onChange,
  onClear,
  onKeyDown,
  placeholder,
  removeErrorStyling,
  tooltip,
  type,
  value,
  withLabel,
}) {
  const isInvalid = !!fieldError?.fieldHighlighted;

  const additionalInputClassName = (() => {
    switch (true) {
      case isDisabled:
        return 'tw-bg-grey-025 tw-text-grey-400 tw-border-grey-200 hover:tw-border-grey-200 focus-within:tw-border-grey-200 hover:focus-within:tw-border-grey-200';
      case isInvalid:
        return 'tw-border-red-600 hover:tw-border-red-600 focus-within:tw-border-red-600 hover:focus-within:tw-border-red-600';
      default:
        return 'tw-border-grey-300 hover:tw-border-grey-400 focus-within:tw-border-blue-300 hover:focus-within:tw-border-blue-300 placeholder:tw-text-grey-300';
    }
  })();

  return (
    <div
      className={classNames('text-field', className && `field--${className}`)}
    >
      <div className={classNames('form-group', customMargin)}>
        {withLabel && (
          <label className='field__label tw-font-medium' htmlFor={name}>
            {label} {isRequired && '*'}
            {!!tooltip && (
              <Tooltip placement='top' tooltip={tooltip} trigger='hover'>
                <CircleQuestion />
              </Tooltip>
            )}
          </label>
        )}
        <div className='field__input-container pos-rel tw-flex tw-items-center'>
          <input
            autoFocus={autoFocus}
            className={classNames(
              'field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
              additionalInputClassName,
              inputClassName,
            )}
            disabled={isDisabled}
            id={voca.snakeCase(name)}
            maxLength={maxLength}
            min={min}
            name={name}
            onChange={onChange}
            onFocus={removeErrorStyling}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={inputRef}
            type={type}
            value={value}
          />
          {clearable && (
            <CloseIcon
              className='field__clear [&_polygon]:tw-fill-grey-700'
              height={24}
              onClick={onClear}
              width={24}
            />
          )}
        </div>
      </div>
    </div>
  );
}

TextField.propTypes = {
  isRequired: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

TextField.defaultProps = {
  type: 'text',
  isRequired: false,
  isDisabled: false,
  removeErrorStyling: () => {},
  onKeyDown: () => undefined,
  placeholder: '',
  withLabel: true,
  maxLength: 'unset',
};
